/**
 * "Link Pipedrive deal" and "Unlink" — fix a deal's Pipedrive link by hand.
 *
 * A Pipedrive deal reaches a Hickory deal only through automatic matching, and a lost
 * or archived Pipedrive deal settles the deal it sits on, so a wrong match must be
 * fixable here. The Pipedrive twin of "Link ServiceTitan work" (`link-st-work-dialog`),
 * on every brand.
 *
 * ## What Confirm actually does
 *
 * The API writes ONE audit row and hands the click to the data platform, which writes
 * the deal ↔ Pipedrive deal link and never re-points the deal's customer or address. The
 * dialog polls until the link has landed, then closes; after 20 s it closes anyway and
 * keeps checking, so a late landing still refreshes the deal and a late refusal still
 * shows. A Pipedrive deal another deal of this brand holds is a MOVE: the dialog says
 * so, and Confirm takes it off that deal.
 *
 * "Unlink" takes the Pipedrive deal off this deal after a confirm. It never comes back on
 * a Pipedrive update; the data platform moves it to another of the customer's deals, or
 * a new one.
 */

import { useEffect, useRef, useState } from "react";
import { secondsToMilliseconds } from "date-fns";
import { Search } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveOverlay } from "@/components/ui/responsive-overlay";
import {
  fetchPdLinkStatus,
  followLate,
  useLinkPdDeal,
  usePdLinkPreflight,
  usePdLinkStatus,
  usePdUnlinkPreflight,
  useUnlinkPdDeal,
  type PdLinkPreflightInput,
  type PdLinkStatus,
} from "@/hooks/use-deal-board";

/** How long the dialog waits for a click to land before letting go. */
const WAIT_SECONDS = 20;

function sameInput(
  made: PdLinkPreflightInput | undefined,
  now: PdLinkPreflightInput,
): boolean {
  return (
    made !== undefined &&
    made.dealId === now.dealId &&
    made.reference === now.reference
  );
}

/** Toasts a landed or refused click. */
function announce(status: PdLinkStatus) {
  if (status.state === "done") {
    toast.success(
      status.action === "unlink"
        ? "Pipedrive deal unlinked"
        : "Linked to the Pipedrive deal",
      {
        description:
          status.action === "unlink"
            ? "It moved to another deal for this customer, or a new one."
            : "The deal keeps its customer and address.",
      },
    );
  } else {
    toast.error("It was refused", {
      description:
        status.error ?? "Canonical resolution rejected it. Tell engineering.",
    });
  }
}

/**
 * Follows a recorded click until it lands: toasts the outcome, calls `settled`, then
 * `close`s. The dialog waits at most 20 s; after that `followLate` keeps checking, so a
 * late landing still refreshes the deal and a late refusal is still shown.
 */
function usePlacement(
  dealId: string | null,
  close: () => void,
  settled: () => void = () => {},
) {
  const [placing, setPlacing] = useState<string | null>(null);
  const status = usePdLinkStatus(dealId, placing);
  const queryClient = useQueryClient();
  const callbacks = useRef({ close, settled });
  callbacks.current = { close, settled };

  useEffect(() => {
    if (!placing || !status.data || status.data.state === "pending") return;
    announce(status.data);
    setPlacing(null);
    callbacks.current.settled();
    callbacks.current.close();
  }, [placing, status.data]);

  // A real timer: an unchanged "pending" response never re-runs the effect above.
  useEffect(() => {
    if (!placing || !dealId) return;
    const timer = setTimeout(() => {
      toast("Recorded", { description: "The deal will update shortly." });
      const { settled: lateSettled } = callbacks.current;
      followLate(
        () => fetchPdLinkStatus(queryClient, dealId, placing),
        (late) => {
          announce(late);
          lateSettled();
        },
      );
      setPlacing(null);
      callbacks.current.close();
    }, secondsToMilliseconds(WAIT_SECONDS));
    return () => clearTimeout(timer);
  }, [placing, dealId, queryClient]);

  return { placing, setPlacing };
}

export interface LinkPipedriveDealTarget {
  dealId: string;
  dealLabel: string;
}

export function LinkPipedriveDealDialog({
  target,
  onOpenChange,
}: {
  target: LinkPipedriveDealTarget | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [reference, setReference] = useState("");
  // The idempotency key: one per open, sent with the link.
  const [linkKey, setLinkKey] = useState(() => crypto.randomUUID());
  const [movedFrom, setMovedFrom] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const dealId = target?.dealId ?? null;
  const preflight = usePdLinkPreflight();
  const link = useLinkPdDeal();
  const { placing, setPlacing } = usePlacement(
    dealId,
    () => onOpenChange(false),
    () => {
      // The deal it came off changed too (its position, or it is gone).
      if (movedFrom)
        void queryClient.invalidateQueries({
          queryKey: ["dcc", "deal", movedFrom],
        });
    },
  );

  // Reset on every OPEN.
  useEffect(() => {
    if (!target) return;
    setReference("");
    setLinkKey(crypto.randomUUID());
    setMovedFrom(null);
    setPlacing(null);
    preflight.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset is keyed on the open edge only
  }, [target?.dealId]);

  const input: PdLinkPreflightInput = {
    dealId: dealId ?? "",
    reference: reference.trim(),
  };
  const current = sameInput(preflight.variables, input);
  const found = current ? preflight.data : undefined;
  const lookupFailed = current && preflight.isError;
  const record = found?.record ?? null;
  const moving = found?.requires_move === true && found.refusal === null;
  const submitting = link.isPending || placing !== null;
  const ready = found !== undefined && found.refusal === null;

  function lookUp() {
    if (!dealId || !reference.trim()) {
      preflight.reset();
      return;
    }
    preflight.mutate(input);
  }

  function submit() {
    if (!target || !ready || submitting) return;
    link.mutate(
      {
        dealId: target.dealId,
        linkId: linkKey,
        reference: reference.trim(),
        move: moving,
      },
      {
        onSuccess: (result) => {
          setMovedFrom(moving ? (found?.linked_deal_id ?? null) : null);
          setPlacing(result.link_id);
        },
        // The server's own sentence names the specific Pipedrive fact.
        onError: (error) =>
          toast.error("Couldn't link it", { description: error.message }),
      },
    );
  }

  if (!target) return null;

  return (
    <ResponsiveOverlay
      kind="dialog"
      open
      onOpenChange={(open) => !submitting && onOpenChange(open)}
      title="Link Pipedrive Deal"
      description={target.dealLabel}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            aria-busy={submitting}
            disabled={!ready || submitting}
          >
            {placing
              ? "Linking…"
              : submitting
                ? "Recording…"
                : moving
                  ? "Move to This Deal"
                  : "Link"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="pd-link-reference">
            Pipedrive deal link or number
          </Label>
          <div className="flex gap-2">
            <Input
              id="pd-link-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  lookUp();
                }
              }}
              placeholder="Paste the Pipedrive deal link"
              disabled={submitting}
              autoFocus
              className="max-md:h-11"
            />
            <Button
              type="button"
              variant="outline"
              onClick={lookUp}
              disabled={!reference.trim() || preflight.isPending || submitting}
              aria-busy={preflight.isPending}
              className="max-md:h-11"
            >
              <Search className="mr-1.5 h-3.5 w-3.5" />
              Look Up
            </Button>
          </div>
        </div>

        {lookupFailed ? (
          <div
            className="rounded-(--radius) border border-destructive/40 bg-destructive/5 p-3 text-sm"
            role="alert"
          >
            <p className="font-medium">Couldn't look it up</p>
            <p className="mt-1 text-xs opacity-90">
              {preflight.error?.message}
            </p>
            <Button
              variant="link"
              size="sm"
              className="mt-1 h-auto p-0 text-xs max-md:min-h-11"
              onClick={lookUp}
            >
              Try Again
            </Button>
          </div>
        ) : null}

        {found?.refusal ? (
          <div className="rounded-(--radius) border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <p className="font-medium">Can't link this</p>
            <p className="mt-1 text-xs opacity-90">{found.refusal_detail}</p>
            {found.linked_deal_id ? (
              <a
                href={`/deals/${found.linked_deal_id}`}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-block text-xs underline"
              >
                Open that deal
              </a>
            ) : null}
          </div>
        ) : null}

        {record && !found?.refusal ? (
          <div
            className="space-y-1 rounded-(--radius) border bg-muted/50 p-3 text-sm"
            role="status"
          >
            <p className="font-medium">
              Pipedrive deal {record.pd_deal_id}
              {record.title ? ` — ${record.title}` : ""}
            </p>
            {record.status ? (
              <p className="text-xs text-muted-foreground">
                {record.status}
                {record.is_archived ? " · archived" : ""}
              </p>
            ) : null}
            <p className="pt-1 text-xs text-muted-foreground">
              Confirming links this deal to that Pipedrive deal. The deal keeps
              its customer and address.
            </p>
          </div>
        ) : null}

        {moving ? (
          <div
            className="rounded-(--radius) border border-warning/50 p-3 text-sm"
            role="note"
          >
            Pipedrive deal #{record?.pd_deal_id ?? ""} is linked to{" "}
            <a
              href={`/deals/${found?.linked_deal_id ?? ""}`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              another deal
            </a>
            . Confirming moves it to this deal: that deal loses it, and is
            removed if it was all that deal held.
          </div>
        ) : null}
      </div>
    </ResponsiveOverlay>
  );
}

/** "Unlink" on one Pipedrive deal the deal holds, behind a confirm. */
export function UnlinkPipedriveDealButton({
  dealId,
  pdDealId,
}: {
  dealId: string;
  pdDealId: string;
}) {
  const [open, setOpen] = useState(false);
  const [linkKey, setLinkKey] = useState(() => crypto.randomUUID());
  const unlink = useUnlinkPdDeal();
  const { placing, setPlacing } = usePlacement(dealId, () => setOpen(false));
  // Asked when the confirm opens: a refused Unlink (the deal's only record, a brand that has left
  // Pipedrive) is explained in the server's words, never offered.
  const check = usePdUnlinkPreflight(dealId, pdDealId, open);
  const refusal = check.data?.refusal ? check.data.refusal_detail : null;
  const busy = unlink.isPending || placing !== null;
  // Only a check that came back clear offers Unlink; a failed one says so.
  const blocked = busy || !check.isSuccess || refusal !== null;

  function confirm() {
    if (blocked) return;
    unlink.mutate(
      { dealId, linkId: linkKey, pdDealId },
      {
        onSuccess: (result) => setPlacing(result.link_id),
        onError: (error) =>
          toast.error("Couldn't unlink it", { description: error.message }),
      },
    );
  }

  return (
    <>
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-sm text-muted-foreground max-md:min-h-11"
        onClick={() => {
          setLinkKey(crypto.randomUUID());
          setOpen(true);
        }}
        aria-label={`Unlink Pipedrive deal ${pdDealId}`}
      >
        Unlink
      </Button>
      <ResponsiveOverlay
        kind="dialog"
        open={open}
        onOpenChange={(next) => !busy && setOpen(next)}
        title={`Unlink Pipedrive Deal ${pdDealId}?`}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            {/* Stays open while the click lands; the poll closes it. */}
            <Button
              variant="destructive"
              onClick={confirm}
              aria-busy={busy}
              disabled={blocked}
            >
              {busy ? "Unlinking…" : "Unlink"}
            </Button>
          </>
        }
      >
        {check.isError ? (
          <div
            className="rounded-(--radius) border border-destructive/40 bg-destructive/5 p-3 text-sm"
            role="alert"
          >
            <p>Couldn't check this Unlink. {check.error.message}</p>
            <Button
              variant="link"
              size="sm"
              className="mt-1 h-auto p-0 text-xs max-md:min-h-11"
              onClick={() => void check.refetch()}
            >
              Try Again
            </Button>
          </div>
        ) : refusal ? (
          <p
            className="rounded-(--radius) border border-destructive/40 bg-destructive/5 p-3 text-sm"
            role="alert"
          >
            {refusal}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            This deal stops holding it, and Pipedrive updates never put it back.
            It moves to another deal for this customer, or a new one.
          </p>
        )}
      </ResponsiveOverlay>
    </>
  );
}
